'use client';

import type { ComponentType } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Promoted gradient pill CTA — the shared visual used by the
 * search "Open discovery" button and the widget-header "Remix"
 * button. Indigo → violet → pink → orange gradient that flips
 * direction on hover. Keep both callers pointed here so the look
 * stays in sync.
 */
export function GradientPillButton({
  icon: Icon,
  label,
  onClick,
  loading = false,
  disabled = false,
  iconOnly = false,
  className,
  'data-testid': testid,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
  /** Swaps the lead icon for a spinner and disables the button. */
  loading?: boolean;
  disabled?: boolean;
  /** Hide the label (icon-only) — used on narrow widget headers. */
  iconOnly?: boolean;
  className?: string;
  'data-testid'?: string;
}) {
  const LeadIcon = loading ? Loader2 : Icon;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      // Icon-only mode still needs an accessible name + hover hint.
      aria-label={iconOnly ? label : undefined}
      title={iconOnly ? label : undefined}
      data-testid={testid}
      className={cn(
        'group relative inline-flex shrink-0 items-center gap-1.5 overflow-hidden rounded-full px-3 py-1 text-[11px] font-semibold text-white',
        'shadow-[0_4px_18px_-6px_rgba(99,102,241,0.65)] transition-transform active:scale-[0.97]',
        'bg-[linear-gradient(135deg,#6366f1_0%,#a855f7_45%,#ec4899_75%,#f97316_100%)]',
        'disabled:opacity-60 disabled:active:scale-100',
        iconOnly && 'px-1.5',
        className,
      )}
    >
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100',
          'bg-[linear-gradient(135deg,#f97316_0%,#ec4899_25%,#a855f7_55%,#6366f1_100%)]',
        )}
      />
      <LeadIcon className={cn('relative z-[1] h-3.5 w-3.5', loading && 'animate-spin')} />
      {!iconOnly && <span className="relative z-[1]">{label}</span>}
    </button>
  );
}
